import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// 🌈 Enhanced Status Badge Component (matches CSS design)
const StatusBadge = ({ status }) => {
  const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
  let style = {
    padding: "6px 12px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-block",
    textTransform: "capitalize",
    color: "#fff",
    boxShadow: "0 0 8px rgba(0,0,0,0.3)",
  };

  switch (normalized) {
    case "available":
      style.background = "linear-gradient(135deg, #16A34A, #22C55E)";
      style.boxShadow = "0 0 10px rgba(34,197,94,0.5)";
      break;
    case "assigned_to_agent":
      style.background = "linear-gradient(135deg, #0EA5E9, #38BDF8)";
      style.boxShadow = "0 0 10px rgba(14,165,233,0.5)";
      break;
    case "assigned_to_customer":
      style.background = "linear-gradient(135deg, #FACC15, #FBBF24)";
      style.color = "#1E293B";
      style.boxShadow = "0 0 10px rgba(250,204,21,0.6)";
      break;
    case "in_repair":
      style.background = "linear-gradient(135deg, #DC2626, #EF4444)";
      style.boxShadow = "0 0 10px rgba(239,68,68,0.5)";
      break;
    case "decommissioned":
      style.background = "linear-gradient(135deg, #6B7280, #9CA3AF)";
      style.boxShadow = "0 0 10px rgba(107,114,128,0.5)";
      break;
    default:
      style.background = "linear-gradient(135deg, #9CA3AF, #D1D5DB)";
      style.color = "#1E293B";
      style.boxShadow = "0 0 10px rgba(156,163,175,0.5)";
  }

  return <span style={style}>{status || "Unknown"}</span>;
};

const POLLING_INTERVAL = 15000; // Poll every 15 seconds

function DevicesListPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const displayedDeviceIds = useRef(new Set());

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  const fetchDevices = useCallback(
    async (isInitialLoad = false) => {
      if (isInitialLoad) setLoading(true);
      else setIsPolling(true);
      setError(null);

      try {
        const apiUrl = `${API_BASE_URL}/devices/devices.php`;
        const response = await fetch(apiUrl);
        if (!response.ok)
          throw new Error(
            `HTTP ${response.status}: ${response.statusText || "Failed to fetch"}`
          );
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const fetched = result.data;

          if (!isInitialLoad) {
            const newOnes = fetched.filter(
              (d) => !displayedDeviceIds.current.has(d.id.toString())
            );
            if (newOnes.length > 0)
              toast.info(
                newOnes.length === 1
                  ? `New device added: ${newOnes[0].device_name}`
                  : `${newOnes.length} new devices added`
              );
          }

          setDevices(fetched);
          displayedDeviceIds.current = new Set(
            fetched.map((d) => d.id.toString())
          );
        } else throw new Error(result.error || "Invalid data");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setIsPolling(false);
      }
    },
    [API_BASE_URL]
  );

  useEffect(() => {
    fetchDevices(true);
    if (location.state?.fromCreate && location.state?.message) {
      toast.success(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [fetchDevices, location, navigate]);

  useEffect(() => {
    if (loading || error) return;
    const interval = setInterval(() => {
      if (!isPolling) fetchDevices(false);
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchDevices, loading, error, isPolling]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      const apiUrl = `${API_BASE_URL}/devices/delete.php?id=${id}`;
      const response = await fetch(apiUrl, { method: "DELETE" });
      const result = await response.json();
      if (result.success) {
        toast.success(`"${name}" deleted successfully`);
        setDevices((prev) => prev.filter((d) => d.id !== id));
      } else throw new Error(result.error);
    } catch (err) {
      toast.error(`Error deleting "${name}": ${err.message}`);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
      }}
    >
      <ToastContainer theme="dark" />

      <div
        style={{
          background: "rgba(255,255,255,0.08)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1000px",
          margin: "0 auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "600" }}>
            Device Management
          </h1>
          <p style={{ color: "#E0E7FF" }}>
            Manage all registered devices in the system.
          </p>
          {isPolling && (
            <small style={{ color: "#93C5FD" }}>Checking for updates...</small>
          )}
        </div>

        {error && (
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid #F87171",
              borderRadius: "10px",
              padding: "15px",
              color: "#FCA5A5",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <Link
            to="/devices/create"
            style={{
              background: "#16A34A",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "600",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            + Add New Device
          </Link>
        </div>

        <div
          style={{
            overflowX: "auto",
            background: "rgba(255,255,255,0.9)",
            borderRadius: "10px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              color: "#1E293B",
            }}
          >
            <thead>
              <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                <th style={{ padding: "14px", textAlign: "left" }}>ID</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Device Name</th>
                <th style={{ padding: "14px", textAlign: "left" }}>IMEI</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(5)].map((__, j) => (
                      <td
                        key={j}
                        style={{
                          padding: "12px",
                          background: "#F1F5F9",
                        }}
                      >
                        <div
                          style={{
                            height: "14px",
                            background: "#CBD5E1",
                            borderRadius: "4px",
                            width: "70%",
                            margin: "auto",
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : devices.length > 0 ? (
                devices.map((device, i) => (
                  <tr
                    key={device.id}
                    style={{
                      background: i % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <td style={{ padding: "12px" }}>{device.id}</td>
                    <td style={{ padding: "12px" }}>{device.device_name || "N/A"}</td>
                    <td style={{ padding: "12px" }}>{device.imei || "N/A"}</td>
                    <td style={{ padding: "12px" }}>
                      <StatusBadge status={device.status} />
                    </td>
                    <td style={{ padding: "12px" }}>
                      <button
                        onClick={() => navigate(`/devices/view/${device.id}`)}
                        style={{
                          background: "#2563EB",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          marginRight: "6px",
                          cursor: "pointer",
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => navigate(`/devices/edit/${device.id}`)}
                        style={{
                          background: "#CA8A04",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          marginRight: "6px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() =>
                          handleDelete(device.id, device.device_name)
                        }
                        style={{
                          background: "#DC2626",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#475569",
                    }}
                  >
                    No devices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DevicesListPage;
